import * as React from 'react';

import { branch, compose } from 'recompose';

import user from 'js/lib/user';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import { useShowMonthlyToAnnualUpgradeOnXDP } from 'bundles/coursera-plus/utils/plusMonthlyToAnnualUpgradeUtils';
import type { EligibilityCheckProps } from 'bundles/coursera-plus/utils/withCourseraPlusProductEligibilityCheck';
import withCourseraPlusProductEligibilityCheck from 'bundles/coursera-plus/utils/withCourseraPlusProductEligibilityCheck';
import type { PropsFromCourseraPlusProductOwnerships } from 'bundles/coursera-plus/utils/withCourseraPlusProductOwnerships';
import withCourseraPlusProductOwnerships from 'bundles/coursera-plus/utils/withCourseraPlusProductOwnerships';
import type { PropsFromWithEnrollment } from 'bundles/enroll/components/xdp/withEnrollment';
import withEnrollment from 'bundles/enroll/components/xdp/withEnrollment';
import inServerContext from 'bundles/ssr/util/inServerContext';
import { useCoursePageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffCourseContext';
import { useSpecializationPageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffSpecializationContext';
import CourseraPlusOwnershipContext from 'bundles/unified-description-page-common/contexts/consumer/CourseraPlusOwnershipContext';

type PropsToInnerProvider = EligibilityCheckProps &
  PropsFromWithEnrollment &
  PropsFromCourseraPlusProductOwnerships & {
    children: React.ReactNode;
  };

type PropsToDataFetcher = {
  s12nId?: string;
  courseId?: string;
  isSpecialization: boolean;
};

const CourseraPlusOwnershipDataProviderInner = ({
  children,
  isCourseraPlusEligibleProduct,
  enrollmentAvailableChoices,
  ownsCourseraPlus,
  ownsCourseraLite,
}: PropsToInnerProvider) => {
  const isSubscribedToCPlusOrLite =
    enrollmentAvailableChoices?.canEnrollThroughCourseraPlus || ownsCourseraPlus || ownsCourseraLite;
  const isUserEligibleToSeeCta =
    enrollmentAvailableChoices?.canSubscribeOrEnrollInCourseraPlusEligibleProduct || isCourseraPlusEligibleProduct;
  const { showPlusMonthlyToAnnualUpgradeOnXDP, isEligibleForPlusMonthlyToAnnualUpgrade } =
    useShowMonthlyToAnnualUpgradeOnXDP();

  return (
    <CourseraPlusOwnershipContext.Provider
      value={{
        isSubscribedToCPlusOrLite,
        isUserEligibleToSeeCta,
        isCourseraPlusEligibleProduct: isUserEligibleToSeeCta,
        isSubscribedToCourseraLite: ownsCourseraLite,
        showPlusMonthlyToAnnualUpgradeButton: Boolean(isUserEligibleToSeeCta && showPlusMonthlyToAnnualUpgradeOnXDP),
        isEligibleForPlusMonthlyToAnnualUpgrade: Boolean(
          isUserEligibleToSeeCta && isEligibleForPlusMonthlyToAnnualUpgrade
        ),
      }}
    >
      {children}
    </CourseraPlusOwnershipContext.Provider>
  );
};

const CourseraPlusOwnershipDataFetcher = compose<PropsToInnerProvider, PropsToDataFetcher>(
  withEnrollment(true),
  // If EAC indicates the learner can subscribe or enroll into a C+ eligible product, then we don't need to fetch C+ product eligibility
  branch<PropsFromWithEnrollment>(
    ({ enrollmentAvailableChoices }) => !enrollmentAvailableChoices?.canSubscribeOrEnrollInCourseraPlusEligibleProduct,
    withCourseraPlusProductEligibilityCheck<PropsToDataFetcher>(true)
  ),
  // If EAC indicates the learner can enroll through C+, then we already know that they own C+
  // Otherwise, if the learner is already enrolled, EAC doesn't know whether they are enrolled through C+, and we have to fetch C+ ownership
  branch<PropsFromWithEnrollment & PropsToDataFetcher & EligibilityCheckProps>(
    ({ enrollmentAvailableChoices }) => !inServerContext && !enrollmentAvailableChoices?.canEnrollThroughCourseraPlus,
    compose<PropsToInnerProvider, PropsToDataFetcher & EligibilityCheckProps>(withCourseraPlusProductOwnerships(true))
  )
)(CourseraPlusOwnershipDataProviderInner);

export const CourseCourseraPlusOwnershipDataProvider = ({ children }: { children: React.ReactNode }) => {
  const pageData = useCoursePageData();

  if (pageData?.id) {
    return (
      <ErrorBoundaryWithTagging
        componentName="CourseCourseraPlusOwnershipDataProvider"
        errorComponent={<>{children}</>}
      >
        <CourseraPlusOwnershipDataFetcher courseId={pageData.id} isSpecialization={false}>
          {children}
        </CourseraPlusOwnershipDataFetcher>
      </ErrorBoundaryWithTagging>
    );
  } else {
    return <>{children}</>;
  }
};

export const SpecializationCourseraPlusOwnershipDataProvider = ({ children }: { children: React.ReactNode }) => {
  const pageData = useSpecializationPageData();

  if (pageData?.id) {
    return (
      <ErrorBoundaryWithTagging
        componentName="SpecializationCourseraPlusOwnershipDataProvider"
        errorComponent={<>{children}</>}
      >
        <CourseraPlusOwnershipDataFetcher s12nId={pageData.id} isSpecialization>
          {children}
        </CourseraPlusOwnershipDataFetcher>
      </ErrorBoundaryWithTagging>
    );
  } else {
    return <>{children}</>;
  }
};
